/**
 * Tabs toggler.
 *
 * Liquid server-renders the initially active tab with the `active` class, so
 * the correct panel is visible on first paint (no FOUC). Visibility is driven
 * entirely by the `.active` class via CSS — this script never writes inline
 * `style.display`, which keeps click work down to a handful of class/attribute
 * toggles.
 *
 * Markup (simplified):
 *   <div class="tabs__inner">
 *     <div class="tabs__nav">
 *       <button class="tab-link [active]" onclick="openTab(event, 'tab-item-<id>-<n>')">…</button>
 *       …
 *     </div>
 *     <div class="tabs__panels">
 *       <div id="tab-item-<id>-<n>" class="tab-content [active]">…</div>
 *       …
 *     </div>
 *   </div>
 *
 * @param {MouseEvent} event
 * @param {string} panelId
 */
function openTab(event, panelId) {
  event.preventDefault();

  const button = /** @type {HTMLElement} */ (event.currentTarget);

  // Fast no-op: re-clicking the already-active tab does zero DOM work.
  if (button.classList.contains('active')) return;

  const panel = document.getElementById(panelId);
  if (panel === null) return;

  // Scope to this tabs block so multiple tabs on the same page don't collide.
  const container = panel.closest('.tabs__inner');
  if (container === null) return;

  // Only the previously-active pair needs clearing; every other trigger/panel
  // is already in the correct inactive state. `querySelector` stops at the
  // first match, giving us O(1) DOM reads + O(1) writes per click.
  const prevButton = container.querySelector('.tab-link.active');
  const prevPanel = container.querySelector('.tab-content.active');

  if (prevButton !== null && prevButton !== button) {
    prevButton.classList.remove('active');
    prevButton.setAttribute('aria-selected', 'false');
    prevButton.setAttribute('tabindex', '-1');
  }

  if (prevPanel !== null && prevPanel !== panel) {
    prevPanel.classList.remove('active');
  }

  button.classList.add('active');
  button.setAttribute('aria-selected', 'true');
  button.setAttribute('tabindex', '0');

  panel.classList.add('active');
}

// Expose globally so the inline `onclick="openTab(…)"` handlers can reach it.
window.openTab = openTab;
